var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import LoadingContainer from '@/shared/components/loading/LoadingContainer';
import { injectStateService } from '@/shared/services/StateService';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { TaskQueryKey } from '@/shared/hooks/task/queryKey';
import { showWhenPermission } from '@/shared/components/security';
import { WeekPlannerHeader } from './partial/WeekPlannerHeader';
import AuthService, { functionalities, privileges } from '@/shared/services/AuthService';
import { useGetWeekPlanner } from '@/shared/hooks/task/useGetWeekPlanner';
import { DragDropContext, Draggable, Droppable, } from '@hello-pangea/dnd';
import moment from 'moment';
import WeekPlannerElementComponent from '@/modules/tasks/weekplanner/partial/WeekPlannerElement';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import TaskDetailsModal from '@/modules/tasks/taskDetails/TaskDetailsModal';
import dateFormatService from '@/shared/services/DateFormatService';
var StorageKey = 'WeekPlannerParams';
var getEmptyState = function () {
    var executionDateFrom = moment
        .utc()
        .startOf('day')
        .startOf('week');
    return {
        taskTemplates: [],
        userId: AuthService.getUserId(),
        status: null,
        executionDateFrom: executionDateFrom.format('YYYY-MM-DD'),
        executionDateUntil: executionDateFrom
            .clone()
            .add(6, 'days')
            .format('YYYY-MM-DD'),
    };
};
var getFiltersFromRouteOrLocalStorage = function (props) {
    var emptyState = getEmptyState();
    if (!!props.filters && props.filters.executionDateFrom && props.filters.userId) {
        return {
            userId: props.filters.userId,
            executionDateFrom: props.filters.executionDateFrom,
            executionDateUntil: moment(props.filters.executionDateFrom)
                .add(6, 'days')
                .format('YYYY-MM-DD'),
        };
    }
    var storageString = sessionStorage.getItem(StorageKey);
    if (!storageString) {
        return emptyState;
    }
    try {
        var filterObject = JSON.parse(storageString);
        return __assign(__assign({}, emptyState), filterObject);
    }
    catch (error) {
        return emptyState;
    }
};
var WeekPlannerContainer = function (props) {
    var _a, _b;
    var queryClient = useQueryClient();
    var _c = useState([]), columns = _c[0], setColumns = _c[1];
    var _d = useState((_a = getFiltersFromRouteOrLocalStorage(props)) !== null && _a !== void 0 ? _a : {}), activeFilters = _d[0], setActiveFilters = _d[1];
    var _e = useGetWeekPlanner(activeFilters), data = _e.data, isLoading = _e.isLoading, onRefetch = _e.refetch;
    var buildColumn = function (items, index, date) {
        var totalTime = items.map(function (i) { return i.executionDuration; }).reduce(function (a, b) { return a + b; }, 0);
        return {
            index: index,
            date: date,
            totalTime: totalTime,
            items: items,
            id: date.format('YYYY-MM-DD'),
            title: date.format('dddd'),
            subtitle: dateFormatService.formatDate(date),
        };
    };
    useEffect(function () {
        var content = [];
        var base = moment(activeFilters.executionDateFrom).startOf('week');
        var _loop_1 = function (i) {
            var day = base.clone().add(i, 'days');
            var nextDay = base.clone().add(i + 1, 'days');
            var items = (data || []).filter(function (x) {
                return moment(x.executionDate).isBetween(day, nextDay, 'days', '[)');
            });
            content.push(buildColumn(items, i, day));
        };
        for (var i = 0; i < 5; i++) {
            _loop_1(i);
        }
        setColumns(content);
    }, [data, activeFilters.executionDateFrom]);
    var _f = useState({ task: undefined, open: false }), taskDetailModal = _f[0], setTaskDetailModal = _f[1];
    var openTaskDetailModal = function (task) { return setTaskDetailModal({ task: task, open: true }); };
    var closeTaskDetailModal = function (tasks) {
        setTaskDetailModal({ task: undefined, open: false });
        if (!tasks)
            return;
        var map = {};
        for (var _i = 0, tasks_1 = tasks; _i < tasks_1.length; _i++) {
            var task = tasks_1[_i];
            map[task.id] = task;
        }
        var newColumns = columns.map(function (c) {
            return buildColumn(c.items.map(function (i) { return (i.id in map ? __assign(__assign({}, i), map[i.id]) : i); }), c.index, c.date);
        });
        setColumns(newColumns);
    };
    var refetch = function () {
        if (isLoading) {
            var queryKey = [TaskQueryKey.WeekPlanner];
            queryClient.cancelQueries(queryKey);
            queryClient.invalidateQueries({ queryKey: queryKey });
        }
        return onRefetch();
    };
    var setFilter = function (filter) {
        setActiveFilters(function (prev) {
            var newFilter = __assign(__assign({}, prev), filter);
            sessionStorage.setItem(StorageKey, JSON.stringify(newFilter));
            return newFilter;
        });
        setTimeout(function () {
            refetch();
        }, 0);
    };
    var updateExecutionInformation = function (taskId, newExecutionDate) {
        var fromColumn = columns.find(function (c) { return c.items.some(function (i) { return i.id === taskId; }); });
        var toColumn = columns.find(function (c) { return c.id === newExecutionDate; });
        if (fromColumn === toColumn)
            return;
        var task = fromColumn.items.find(function (t) { return t.id === taskId; });
        if (!task)
            return;
        task.executionDate = moment(newExecutionDate);
        var newColumns = __spreadArray([], columns, true);
        var fromItems = fromColumn.items.filter(function (i) { return i.id !== taskId; });
        var toItems = __spreadArray(__spreadArray([], toColumn.items, true), [task], false);
        newColumns[fromColumn.index] = buildColumn(fromItems, fromColumn.index, fromColumn.date);
        newColumns[toColumn.index] = buildColumn(toItems, toColumn.index, toColumn.date);
        setColumns(newColumns);
        return api.base
            .put("".concat(config.appUrls.tasks, "/api/weekPlanner/execution/").concat(taskId, "/").concat(newExecutionDate))
            .then(function (_a) {
            var updatedTask = _a.data;
        }, function (error) {
            handleHttpError(Intl)(error);
        });
    };
    var onDragEnd = function (result) {
        if (!result.destination)
            return;
        var target = result.destination.droppableId;
        var item = result.draggableId;
        return updateExecutionInformation(item, target);
    };
    var renderItem = function (provided, snapshot, item) {
        return (_jsx("div", __assign({ ref: provided.innerRef }, provided.draggableProps, provided.dragHandleProps, { className: "week-planner-box" }, { children: _jsx(WeekPlannerElementComponent, { element: item, isDragging: snapshot.isDragging, openTaskDetailModal: function (task) { return openTaskDetailModal(task); } }) })));
    };
    var renderColumnItems = function (column, dropProvided, dropSnapshot) {
        return (_jsxs("div", __assign({ ref: dropProvided.innerRef }, dropProvided.droppableProps, { children: [_jsxs("div", __assign({ className: "week-planner-header" }, { children: [_jsx("div", { children: column.title }), _jsx("div", __assign({ className: "week-planner-header-sub" }, { children: column.subtitle })), _jsxs("div", __assign({ className: "week-planner-header-total" }, { children: ["(", column.totalTime, "m)"] }))] })), _jsxs("div", __assign({ className: "week-planner-column-body" }, { children: [_jsx("div", { children: dropProvided.placeholder }), column.items.map(function (item, index) { return (_jsx(Draggable, __assign({ draggableId: item.id, index: index }, { children: function (provided, snapshot) { return renderItem(provided, snapshot, item); } }), item.id)); })] }))] })));
    };
    var renderColumn = function (column) {
        return (_jsx("div", __assign({ className: "week-planner-column" }, { children: _jsx(Droppable, __assign({ droppableId: column.id, type: "ITEM" }, { children: function (dropProvided, dropSnapshot) {
                    return renderColumnItems(column, dropProvided, dropSnapshot);
                } })) }), column.id));
    };
    return (_jsxs(_Fragment, { children: [_jsx(WeekPlannerHeader, { activeFilters: activeFilters, setFilter: setFilter, defaultFilters: getEmptyState() }), _jsxs(LoadingContainer, __assign({ isLoading: isLoading && !data }, { children: [taskDetailModal.open && (_jsx(TaskDetailsModal, { show: true, task: { id: (_b = taskDetailModal.task) === null || _b === void 0 ? void 0 : _b.id }, onSave: closeTaskDetailModal, onClose: closeTaskDetailModal })), _jsx("div", __assign({ className: "week-planner" }, { children: _jsx("div", __assign({ className: "week-planner-container" }, { children: _jsx(DragDropContext, __assign({ onDragEnd: onDragEnd }, { children: columns.map(function (column) { return renderColumn(column); }) })) })) }))] }))] }));
};
export default showWhenPermission(functionalities.PlannedTask, privileges.Read)(injectStateService(WeekPlannerContainer));
